import { useState, useEffect } from "react";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Plus, Calendar, Target, TrendingUp, Users, ChevronDown, ChevronUp } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useOrgPermissions } from "@/hooks/useOrgPermissions";

interface Campaign {
  id: string;
  name: string;
  description: string;
  start_date: string;
  end_date: string;
  status: string;
  budget: number;
  goals: any;
}

interface OrgMember {
  user_id: string;
  full_name: string;
  role?: string;
}

interface CampaignMember {
  user_id: string;
  role: string;
  assigned_at: string;
  full_name: string;
}

// ── Campaign members panel ────────────────────────────────────────────────────

function CampaignMembersPanel({
  campaignId,
  orgMembers,
  can,
  currentUserId,
}: {
  campaignId: string;
  orgMembers: OrgMember[];
  can: (mod: any, level?: any) => boolean;
  currentUserId: string | null;
}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [addMemberOpen, setAddMemberOpen] = useState(false);
  const [newMemberUserId, setNewMemberUserId] = useState("");
  const [newMemberRole, setNewMemberRole] = useState("contributor");

  const { data: campaignMembers = [] } = useQuery<CampaignMember[]>({
    queryKey: ["campaign-members", campaignId],
    queryFn: async () => {
      const { data: members } = await (supabase
        .from("campaign_members" as any)
        .select("user_id, role, assigned_at")
        .eq("campaign_id", campaignId) as any);

      if (!members?.length) return [];

      const userIds = (members as any[]).map((m: any) => m.user_id);
      const { data: profiles } = await supabase
        .from("profiles")
        .select("user_id, full_name")
        .in("user_id", userIds);

      const profileMap = Object.fromEntries(
        (profiles || []).map((p: any) => [p.user_id, p.full_name])
      );

      return (members as any[]).map((m: any) => ({
        ...m,
        full_name: profileMap[m.user_id] || "Team member",
      })) as CampaignMember[];
    },
  });

  const addMemberMutation = useMutation({
    mutationFn: async (data: {
      campaign_id: string;
      user_id: string;
      role: string;
      assigned_by: string | null;
    }) => {
      const result = await supabase.functions.invoke("assign-campaign-member", { body: data });
      if (result.error) throw new Error(result.error.message);
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["campaign-members", campaignId] });
      setAddMemberOpen(false);
      setNewMemberUserId("");
      setNewMemberRole("contributor");
      toast({ title: "Member added", description: "Team member added to campaign" });
    },
    onError: (err: any) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  const removeMemberMutation = useMutation({
    mutationFn: async ({ user_id }: { user_id: string }) => {
      const { error } = await (supabase
        .from("campaign_members" as any)
        .delete()
        .eq("campaign_id", campaignId)
        .eq("user_id", user_id) as any);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["campaign-members", campaignId] });
      toast({ title: "Member removed" });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to remove member", variant: "destructive" });
    },
  });

  const unassignedMembers = orgMembers.filter(
    m => !campaignMembers.find(cm => cm.user_id === m.user_id)
  );

  return (
    <div className="mt-4 border-t border-border pt-4">
      <div className="flex items-center justify-between mb-3">
        <span className="text-[12px] font-medium uppercase tracking-wide text-muted-foreground">
          Campaign Team
        </span>
        {can("social_media", "edit") && (
          <button
            onClick={() => setAddMemberOpen(true)}
            className="text-[11.5px] text-[#006B3C] hover:underline font-medium"
          >
            + Add member
          </button>
        )}
      </div>

      {campaignMembers.length === 0 ? (
        <p className="text-justify text-[12px] text-muted-foreground">No team members assigned yet.</p>
      ) : (
        <div className="space-y-2">
          {campaignMembers.map((m) => (
            <div key={m.user_id} className="flex items-center gap-2.5 py-1.5">
              <div className="w-7 h-7 rounded-full bg-[#C8A84B]/20 flex items-center justify-center text-[10px] font-bold text-[#0D2240] flex-shrink-0">
                {m.full_name.split(" ").map((n: string) => n[0]).join("").slice(0, 2).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[12.5px] font-medium truncate">{m.full_name}</div>
                <div className="text-[11px] text-muted-foreground capitalize">{m.role}</div>
              </div>
              {can("social_media", "edit") && (
                <button
                  onClick={() => window.confirm(`Remove ${m.full_name} from this campaign?`) && removeMemberMutation.mutate({ user_id: m.user_id })}
                  disabled={removeMemberMutation.isPending}
                  className="text-[11px] text-red-500 hover:text-red-700 hover:underline flex-shrink-0"
                >
                  Remove
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      <Sheet open={addMemberOpen} onOpenChange={setAddMemberOpen}>
        <SheetContent side="right" className="w-[360px]">
          <SheetHeader>
            <SheetTitle>Add Team Member</SheetTitle>
          </SheetHeader>
          <div className="p-4 space-y-4">
            <div className="space-y-1">
              <Label>Team member</Label>
              <select
                value={newMemberUserId}
                onChange={e => setNewMemberUserId(e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-lg text-[13px] bg-background"
              >
                <option value="">Select team member…</option>
                {unassignedMembers.map(m => (
                  <option key={m.user_id} value={m.user_id}>
                    {m.full_name}{m.role ? `, ${m.role}` : ""}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <Label>Role</Label>
              <select
                value={newMemberRole}
                onChange={e => setNewMemberRole(e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-lg text-[13px] bg-background"
              >
                <option value="contributor">Contributor</option>
                <option value="lead">Campaign Lead</option>
                <option value="reviewer">Reviewer</option>
              </select>
            </div>
            <button
              disabled={!newMemberUserId || addMemberMutation.isPending}
              onClick={() =>
                addMemberMutation.mutate({
                  campaign_id: campaignId,
                  user_id:     newMemberUserId,
                  role:        newMemberRole,
                  assigned_by: currentUserId,
                })
              }
              className="w-full py-2.5 bg-[#006B3C] text-white rounded-lg text-[13px] font-medium hover:bg-[#005A32] disabled:opacity-50"
            >
              {addMemberMutation.isPending ? "Adding…" : "Add to Campaign"}
            </button>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export const CampaignManager = ({ showHeader = true }: { showHeader?: boolean }) => {
  const { orgId, can } = useOrgPermissions();
  const queryClient = useQueryClient();
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [expandedCampaign, setExpandedCampaign] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [budget, setBudget] = useState("");
  const { toast } = useToast();

  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => {
      setCurrentUserId(user?.id ?? null);
    });
  }, []);

  const { data: campaigns = [], refetch: refetchCampaigns } = useQuery<Campaign[]>({
    queryKey: ["campaigns", currentUserId],
    enabled: !!currentUserId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("content_campaigns")
        .select("*")
        .eq("user_id", currentUserId!)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data || []) as Campaign[];
    },
  });

  const { data: orgMembers = [] } = useQuery<OrgMember[]>({
    queryKey: ["assignable-members", orgId],
    enabled: !!orgId,
    queryFn: async () => {
      const { data: members } = await (supabase
        .from("organization_members" as any)
        .select("user_id, roles(name)")
        .eq("org_id", orgId)
        .eq("status", "active") as any);

      if (!members?.length) return [];

      const userIds = (members as any[]).map((m: any) => m.user_id);
      const { data: profiles } = await supabase
        .from("profiles")
        .select("user_id, full_name")
        .in("user_id", userIds);

      const profileMap = Object.fromEntries(
        (profiles || []).map((p: any) => [p.user_id, p.full_name])
      );

      return (members as any[]).map((m: any) => ({
        user_id:   m.user_id,
        full_name: profileMap[m.user_id] || "Team member",
        role:      m.roles?.name,
      })) as OrgMember[];
    },
  });

  const handleCreate = async () => {
    if (!name.trim() || !startDate) {
      toast({
        title: "Missing information",
        description: "Please fill in campaign name and start date",
        variant: "destructive",
      });
      return;
    }

    try {
      if (!currentUserId) throw new Error("Not authenticated");
      const { error } = await supabase.from("content_campaigns").insert({
        user_id:     currentUserId,
        name:        name.trim(),
        description: description.trim(),
        start_date:  startDate,
        end_date:    endDate || null,
        budget:      budget ? parseFloat(budget) : null,
        status:      "draft",
        goals:       {},
      });
      if (error) throw error;
      toast({ title: "Campaign created", description: `${name} has been created` });
      setName(""); setDescription(""); setStartDate(""); setEndDate(""); setBudget("");
      setOpen(false);
      refetchCampaigns();
    } catch (error) {
      console.error("Error creating campaign:", error);
      toast({ title: "Error", description: "Failed to create campaign", variant: "destructive" });
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "active":    return "default";
      case "completed": return "secondary";
      default:          return "outline";
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        {showHeader ? (
          <div>
            <h2 className="text-2xl font-bold">Campaigns</h2>
            <p className="text-muted-foreground">Organize your content into strategic campaigns</p>
          </div>
        ) : <div />}
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="w-4 h-4 mr-2" />
              New Campaign
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create Campaign</DialogTitle>
              <DialogDescription>
                Set up a new content campaign to organize your posts and track performance.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="campaign-name">Campaign Name</Label>
                <Input id="campaign-name" placeholder="Summer Product Launch" value={name} onChange={e => setName(e.target.value)} />
              </div>
              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea id="description" placeholder="Brief description of campaign goals..." value={description} onChange={e => setDescription(e.target.value)} rows={3} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="start-date">Start Date</Label>
                  <Input id="start-date" type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
                </div>
                <div>
                  <Label htmlFor="end-date">End Date</Label>
                  <Input id="end-date" type="date" value={endDate} onChange={e => setEndDate(e.target.value)} />
                </div>
              </div>
              <div>
                <Label htmlFor="budget">Budget (Optional)</Label>
                <Input id="budget" type="number" placeholder="1000" value={budget} onChange={e => setBudget(e.target.value)} />
              </div>
              <Button onClick={handleCreate} className="w-full">Create Campaign</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid gap-4">
        {campaigns.map(campaign => (
          <Card key={campaign.id} className="p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-xl font-semibold mb-1">{campaign.name}</h3>
                {campaign.description && (
                  <p className="text-justify text-sm text-muted-foreground">{campaign.description}</p>
                )}
              </div>
              <Badge variant={getStatusColor(campaign.status) as any}>{campaign.status}</Badge>
            </div>

            <div className="flex items-center gap-6 text-sm">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-muted-foreground" />
                <span>
                  {new Date(campaign.start_date).toLocaleDateString()}
                  {campaign.end_date && `, ${new Date(campaign.end_date).toLocaleDateString()}`}
                </span>
              </div>
              {campaign.budget && (
                <div className="flex items-center gap-2">
                  <Target className="w-4 h-4 text-muted-foreground" />
                  <span>${campaign.budget}</span>
                </div>
              )}
              <button
                onClick={() =>
                  setExpandedCampaign(
                    expandedCampaign === campaign.id ? null : campaign.id
                  )
                }
                className="ml-auto flex items-center gap-1 text-[12px] text-muted-foreground hover:text-foreground transition-colors"
              >
                <Users size={13} />
                Team
                {expandedCampaign === campaign.id
                  ? <ChevronUp size={13} />
                  : <ChevronDown size={13} />}
              </button>
            </div>

            {expandedCampaign === campaign.id && (
              <CampaignMembersPanel
                campaignId={campaign.id}
                orgMembers={orgMembers}
                can={can}
                currentUserId={currentUserId}
              />
            )}
          </Card>
        ))}

        {campaigns.length === 0 && (
          <Card className="p-12 text-center">
            <TrendingUp className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
            <h3 className="text-lg font-semibold mb-2">No campaigns yet</h3>
            <p className="text-justify text-muted-foreground mb-4">
              Create your first campaign to organize your content strategy
            </p>
            <Button onClick={() => setOpen(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Create Campaign
            </Button>
          </Card>
        )}
      </div>
    </div>
  );
};
